import { PromoBlockSkeleton } from './PromoBlockSkeleton'
import { Button, usePromoBlockVariants } from '../../../Theme'
import { LABELS } from '../../lib'

import type { HomepagePromoBlock } from '../../types'

interface PromoBlockProps {
  block: HomepagePromoBlock
  /** Loading state - shows skeleton */
  isLoading?: boolean
}

/**
 * PromoBlock — overlay "banner band" card (TSS-535).
 *
 * A big image fills the card; an orange band (bg-main-color) is overlaid at the
 * bottom with the title / description / button in black text. When the block has
 * a link the whole image is clickable (kept out of the tab order to avoid
 * duplicating the button's anchor), and the band's button is the accessible link.
 */
export function PromoBlock({ block, isLoading = false }: PromoBlockProps) {
  const {
    promoBlockSectionVariants,
    promoBlockContainerVariants,
    promoBlockImageSectionVariants,
    promoBlockImageLinkVariants,
    promoBlockImageVariants,
    promoBlockContentSectionVariants,
    promoBlockContentInnerVariants,
    promoBlockTitleVariants,
    promoBlockDescriptionVariants,
  } = usePromoBlockVariants()

  if (isLoading) {
    return <PromoBlockSkeleton />
  }

  const image = block.image && (
    <img
      src={block.image}
      alt={block.title}
      width={800}
      height={600}
      loading="lazy"
      className={promoBlockImageVariants()}
    />
  )

  return (
    <article className={promoBlockSectionVariants()}>
      <div className={promoBlockContainerVariants()}>
        {block.image && (
          <div className={promoBlockImageSectionVariants()}>
            {block.link ? (
              <a
                href={block.link}
                title={block.title}
                tabIndex={-1}
                aria-hidden="true"
                className={promoBlockImageLinkVariants()}
              >
                {image}
              </a>
            ) : (
              image
            )}
          </div>
        )}

        <div className={promoBlockContentSectionVariants()}>
          <div className={promoBlockContentInnerVariants()}>
            <h3 className={promoBlockTitleVariants()}>
              {block.title}
            </h3>
            {block.description && (
              <p className={promoBlockDescriptionVariants()}>
                {block.description}
              </p>
            )}
            {block.link && (
              <Button variant="promoBlock" size="sm" asChild className="mt-3">
                <a href={block.link}>{LABELS.VIEW_DETAILS}</a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}
